import React from 'react'
import { Box } from '@mui/material'
import LanguageCurrencyDropdownLogic from '../../../LanguageCurrencyDropdown'
import { HeaderLogo } from '../../shared/HeaderLogo'
import { HeaderNavigation } from '../../shared/HeaderNavigation'
import { DealsLink } from '../../shared/DealsLink'
import { SupportLink } from '../../shared/SupportLink'
import { UserDisplay } from '../../shared/UserDisplay'
import { SignInButton } from '../../shared/SignInButton'
import { UserMenu } from '../../shared/UserMenu'

interface CTHeaderProps {
    navLinks: { label: string; href: string; labelKey?: string; icon?: React.ReactNode; isActive?: boolean }[]
    supportHref: string
    options: {
        language: { code: string; label: string; Flag: React.ComponentType<any> }[]
        currency: { code: string; label: string; Flag: React.ComponentType<any>; symbol: string }[]
    }
    selected: {
        language: { code: string; label: string; Flag: React.ComponentType<any> }
        currency: { code: string; label: string; Flag: React.ComponentType<any>; symbol: string }
    }
    menuAnchors: {
        user: null | HTMLElement
    }
    onMenuOpen: (menu: 'user', event: React.MouseEvent<HTMLElement>) => void
    onMenuClose: (menu: 'user') => void
    onSelect: (type: 'language' | 'currency', value: any) => void
    onModalOpen: (modal: 'signIn') => void
    t: (key: string) => string
    tCurrency: (key: string) => string
    user: { signedIn: boolean; email?: string; displayName?: string; firstName?: string }
    userDisplayName: string
    userMenuItems: { label: string; href: string }[]
    onSignOut: () => void
    showLangCurrDropdown: boolean
    hideSignIn?: boolean
    showDeals?: boolean
    locale: string
}

const CTHeader: React.FC<CTHeaderProps> = ({
    navLinks,
    supportHref,
    options,
    selected,
    menuAnchors,
    onMenuOpen,
    onMenuClose,
    onSelect,
    onModalOpen,
    t,
    tCurrency,
    user,
    userMenuItems,
    onSignOut,
    showLangCurrDropdown,
    hideSignIn = false,
    showDeals = true,
    locale,
    userDisplayName
}) => {

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 1,
                    height: 60
                }}
            >
                {/* Left: Logo + Nav */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <HeaderLogo variant="cleartrip" locale={locale} />
                    <HeaderNavigation navLinks={navLinks} variant="cleartrip" t={t} />
                </Box>

                {/* Right: Actions */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2.5
                    }}
                >
                    {/* Language/Currency Dropdown */}
                    {showLangCurrDropdown && (
                        <Box sx={{ mr: 1 }}>
                            <LanguageCurrencyDropdownLogic
                                variant="cleartrip"
                                selectedLanguage={selected.language}
                                selectedCurrency={selected.currency}
                                languageOptions={options.language}
                                currencyOptions={options.currency}
                                onLanguageSelect={lang => onSelect('language', lang)}
                                onCurrencySelect={curr => onSelect('currency', curr)}
                                t={t}
                                tCurrency={tCurrency}
                            />
                        </Box>
                    )}

                    <DealsLink locale={locale} t={t} hidden={!showDeals} variant="cleartrip" />

                    <SupportLink href={supportHref} t={t} variant="cleartrip" />

                    {user.signedIn && !hideSignIn ? (
                        <UserDisplay
                            userDisplayName={userDisplayName}
                            onClick={e => onMenuOpen('user', e)}
                            variant="cleartrip"
                        />
                    ) : (
                        <SignInButton onClick={() => onModalOpen('signIn')} t={t} variant="cleartrip" hidden={hideSignIn} />
                    )}
                    <UserMenu
                        open={!!menuAnchors.user && user.signedIn}
                        anchorEl={menuAnchors.user}
                        menuItems={userMenuItems}
                        onClose={() => onMenuClose('user')}
                        onSignOut={onSignOut}
                        t={t}
                        variant="cleartrip"
                    />
                </Box>
            </Box>
        </>
    )
}

export { CTHeader }
export type { CTHeaderProps }
